import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { fetchFlowsConsolidated, fetchFinancialFlows, fetchFlowsYears } from 'views/finance/moduleServices/flow_requests';
import { useFinanceProfile } from '../moduleServices/finance_profilecontext';

import BoardFilterFlow from './viewComponents/Board/BoardFilterFlow';
import BoardChartWaterfall from './viewComponents/Board/BoardChartWaterfall';
import BoardChartCategoryBar from './viewComponents/Board/BoardChartCategoryBar';
import BoardDynamicTable from './viewComponents/Board/BoardDynamicTable';
// ###### MAIN COMPONENT
const ViewFlowBoard = ({ flowsUpdateRef, onFlowClick, yearsRange }) => {
  const { profile } = useFinanceProfile();
  const defaultCategL0 = profile?.default_categ_l0 || 'All';
  // ###### STATES
  const [selectedFlowCategL0, setSelectedFlowCategL0] = useState(defaultCategL0);
  const [selectedRangeType, setSelectedRangeType] = useState('last 30 days');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedYears, setSelectedYears] = useState(String(new Date().getFullYear()));
  const [selectedMonths, setSelectedMonths] = useState(String(new Date().getMonth() + 1).padStart(2, '0'));
  const [flowsConsolidated, setflowsConsolidated] = useState(null);
  const [loadingConsolidated, setLoadingConsolidated] = useState(false);
  const [flows, setFlows] = useState([]);
  const [loadingFlows, setLoadingFlows] = useState(false);
  const [selectedFlowType, setSelectedFlowType] = useState('Out');
  const [chartData, setChartData] = useState([]);
  const [flowsYears, setFlowsYears] = useState([]);


  // ###### EFFECTS
  useEffect(() => {
    const loadYears = async () => {
      try {
        const response = await fetchFlowsYears();
        setFlowsYears(response.data.years || []);
      } catch (error) {
        console.error('Error fetching flow years:', error);
      }
    };
    loadYears();
  }, []);

  // ##### Set default year and month to most recent from flowsConsolidated #####
  // useEffect(() => {
  //   if (
  //     flowsConsolidated &&
  //     flowsConsolidated.flows_last_date &&
  //     (!selectedYears || !selectedMonths)
  //   ) {
  //     const [year, month] = flowsConsolidated.flows_last_date.split('-');
  //     if (!selectedYears) setSelectedYears(year);
  //     if (!selectedMonths) setSelectedMonths(month);
  //   }
  // }, [flowsConsolidated, selectedYears, selectedMonths]);

  // ##### LOAD DATA
  const loadDashboardData = useCallback(async () => {
    const loadConsolidated = async (params = {}) => {
      setLoadingConsolidated(true);
      try {
        const response = await fetchFlowsConsolidated(params);
        setflowsConsolidated(response.data);
      } catch (error) {
        console.error('Error fetching finance consolidated:', error);
      } finally {
        setLoadingConsolidated(false);
      }
    };

    const loadAllFlows = async (params = {}) => {
      setLoadingFlows(true);
      const limit = 5000;
      let offset = 0;
      let allFlows = [];
      let hasMore = true;

      while (hasMore) {
        try {
          const response = await fetchFinancialFlows({ ...params, limit, offset });
          const data = response || [];
          allFlows = [...allFlows, ...data];
          hasMore = data.length === limit;
          offset += limit;
        } catch (error) {
          console.error('Error fetching financial flows:', error);
          hasMore = false;
        }
      }
      
      setFlows(allFlows);
      setLoadingFlows(false);
    };

    let params = {};
    if (selectedRangeType === 'by dates' && startDate && endDate) {
      params = { start_date: startDate, end_date: endDate };
    } else if (selectedRangeType === 'by year' && selectedYears) {
      params = { years: selectedYears };
      if (selectedMonths) {
        params.months = selectedMonths;
      }
    } else {
      params = { last30days: true };
    }
    await Promise.all([
      loadConsolidated(params),
      loadAllFlows(params)
    ]);
  }, [selectedRangeType, startDate, endDate, selectedYears, selectedMonths]);

  // Registrar função no flowsUpdateRef
  useEffect(() => {
    if (flowsUpdateRef) {
      flowsUpdateRef.current = loadDashboardData;
    }
  }, [flowsUpdateRef, loadDashboardData]);

  // Carregar dados iniciais
  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  useEffect(() => {
    setSelectedFlowCategL0(defaultCategL0);
  }, [defaultCategL0]);

  const filteredFlows = useMemo(() => {
    return flows.filter(flow => {
      if (selectedFlowCategL0 && selectedFlowCategL0 !== 'All' && flow.categ_l0 !== selectedFlowCategL0) return false;
      if (selectedFlowType && flow.flow_type !== selectedFlowType) return false;
      return true;
    });
  }, [flows, selectedFlowCategL0, selectedFlowType]);

  useEffect(() => {
    if (!filteredFlows.length) return setChartData([]);
    if (!selectedFlowCategL0) return;

    const sorted = Object.values(
      filteredFlows
        .reduce((acc, f) => {
          const cat = f.categ_l1 || 'Not defined';
          const val = Math.abs(+f.std_value || 0);
          acc[cat] = acc[cat] || { 
            category: cat, 
            sum: 0, 
            count: 0,
            subcategories: {}
          };
          acc[cat].sum += val;
          acc[cat].count++;
          
          const subcat = f.categ_l2 || 'Not defined';
          acc[cat].subcategories[subcat] = (acc[cat].subcategories[subcat] || 0) + val;
          
          return acc;
        }, {})
    ).sort((a, b) => b.sum - a.sum);

    setChartData(sorted);
  }, [filteredFlows, selectedFlowCategL0, selectedFlowType]);

  const categories = useMemo(() => {
    const uniqueCategories = [...new Set(flows.map(flow => flow.categ_l0).filter(Boolean))];
    return uniqueCategories.sort();
  }, [flows]);


  // ###### RETURN VIEW
  return (
    <div className="p-0 grid grid-cols-1 md:grid-cols-2 gap-2 w-full">
      <div>
        <BoardFilterFlow
          categories={categories}
          selectedFlowCategL0={selectedFlowCategL0}
          setSelectedFlowCategL0={setSelectedFlowCategL0}
          selectedRangeType={selectedRangeType}
          setSelectedRangeType={setSelectedRangeType}
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
          selectedYears={selectedYears}
          setSelectedYears={setSelectedYears}
          selectedMonths={selectedMonths}
          setSelectedMonths={setSelectedMonths}
          flowsYears={flowsYears}
          yearsRange={yearsRange}
        />

        <BoardChartWaterfall
          flows_values={flowsConsolidated?.flows_categ_l0 || []}
          selectedFlowCategL0={selectedFlowCategL0}
          first_date={flowsConsolidated?.flows_first_date}
          last_date={flowsConsolidated?.flows_last_date}
          qty_days={flowsConsolidated?.flows_qty_days}
          loading={loadingConsolidated}
        />
      </div>

      <BoardChartCategoryBar
        data={chartData}
        selectedFlowCategL0={selectedFlowCategL0}
        selectedFlowType={selectedFlowType}
        setSelectedFlowType={setSelectedFlowType}
        loading={loadingFlows}
      />

      {/* Dynamic Table Card */}
      <BoardDynamicTable
        flows={filteredFlows}
        loading={loadingFlows}
        selectedFlowCategL0={selectedFlowCategL0}
        selectedFlowType={selectedFlowType}
        setSelectedFlowType={setSelectedFlowType}
        onFlowClick={onFlowClick}
      />

    </div>
  );
};

export default ViewFlowBoard;
